<script>
  import { onMount } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import Pagination from '$lib/Pagination.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faRotate,
    faPlus,
    faPencil,
    faTrash,
    faSquarePlus,
    faTrashCan,
    faCopy,
    faHistory,
    faCircleXmark,
    faEllipsisVertical,
    faFileExport,
    faFileImport
  } from '@fortawesome/pro-light-svg-icons';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import FormEditor from './forms/FormEditor.svelte';
  import ConfirmationModal from '$lib/ConfirmationModal.svelte';
  import StudySelectModal from '$lib/StudySelectModal.svelte';
  import AuditLogModal from '$lib/AuditLogModal.svelte';
  import { user, userRole, userRoles, userReadOnly, isReadOnly } from '$stores/sessionStore';
  import { studyProperties } from '$stores/configStore';
  import { hasAuditLogAccess, hasAccessUiAction } from '$utils/permissionUtils.js';
  const browser = typeof window !== 'undefined';

  // Use proxy API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';

  let forms = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let searchText = '';
  let sortField = 'name';
  let sortOrder = 'asc';

  // Lookup data for displaying meaningful labels
  let formTypeOptions = [];
  let userTypeOptions = [];
  let formModelOptions = [];
  let ageRangeOptions = [];
  let weightRangeOptions = [];
  let studyLanguages = [];
  let languageFilterGUID = '';
  let isInitialized = false;
  let previousStudyId = null;

  // Form editor state
  let showFormEditor = false;
  let editingForm = null;

  // Confirmation modal state
  let showDeleteConfirmation = false;
  let formToDelete = null;

  // Study select modal state
  let showStudySelectModal = false;

  // Audit log modal state
  let showAuditLog = false;
  let auditLogForm = null;

  // Menu dropdown state
  let showActionsMenu = false;
  let fileInputRef = null;

  // Add computed property for audit log permissions
  $: canViewAuditLogs = hasAuditLogAccess($userRoles, $studyProperties, $userReadOnly);

  $: isAdmin = hasAccessUiAction(
    'forms.definitionAdminTools',
    $userRoles,
    $studyProperties,
    $userReadOnly
  );

  onMount(async () => {
    //selectedStudy.set(null);
    selectedSite.set(null);
    selectedParticipant.set(null);

    // If store not yet hydrated on navigation, try localStorage
    if (!$selectedStudy && browser) {
      const storedStudy = localStorage.getItem('selectedStudy');
      if (storedStudy) {
        try {
          selectedStudy.set(JSON.parse(storedStudy));
        } catch (error) {
          console.error('Error parsing selectedStudy from localStorage in onMount:', error);
        }
      }
    }

    // Retry briefly to avoid race between navigation and store updates
    let attempts = 0;
    const maxAttempts = 3;
    const initData = async () => {
      if ($selectedStudy?._id) {
        await Promise.all([fetchLookupData(), fetchForms(1)]);
        isInitialized = true;
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(initData, 150);
      }
    };
    initData();
  });

  // When study changes after initialization, refresh lookups and data
  $: if (isInitialized && $selectedStudy?._id && $selectedStudy._id !== previousStudyId) {
    previousStudyId = $selectedStudy._id;
    Promise.all([fetchLookupData(), fetchForms(1)]);
  }

  async function fetchLookupData() {
    try {
      // Fetch all required lookup data in parallel
      const [formTypeRes, userTypeRes, formModelRes, ageRangeRes, weightRangeRes, studyLangRes] = await Promise.all([
        fetch(`${API_BASE_URL}/lookups/FORMTYPE`),
        fetch(`${API_BASE_URL}/lookups/FORMUSERTYPE`),
        fetch(`${API_BASE_URL}/lookups/FORMMODEL`),
        fetch(`${API_BASE_URL}/lookups/AGERANGE`),
        fetch(`${API_BASE_URL}/lookups/WEIGHTRANGE`),
        $selectedStudy?._id ? fetch(`${API_BASE_URL}/languages/study/${$selectedStudy._id}`) : Promise.resolve({ ok: true, json: async () => [] })
      ]);

      // Parse responses
      const [formTypeData, userTypeData, formModelData, ageRangeData, weightRangeData, studyLangData] = await Promise.all([
        formTypeRes.json(),
        userTypeRes.json(),
        formModelRes.json(),
        ageRangeRes.json(),
        weightRangeRes.json(),
        studyLangRes.json()
      ]);

      // Set options
      formTypeOptions = formTypeData.data || [];
      userTypeOptions = userTypeData.data || [];
      formModelOptions = formModelData.data || [];
      ageRangeOptions = ageRangeData.data || [];
      weightRangeOptions = weightRangeData.data || [];
      studyLanguages = Array.isArray(studyLangData) ? studyLangData : [];

    } catch (error) {
      console.error('Error fetching lookup data:', error);
    }
  }

  // Helper functions to get lookup labels
  function getFormTypeLabel(typeGUID) {
    if (!typeGUID) return '-';
    const option = formTypeOptions.find(opt => opt.id === typeGUID);
    return option ? option.label : typeGUID;
  }

  function getUserTypeLabels(userTypeGUIDs) {
    if (!userTypeGUIDs || userTypeGUIDs.length === 0) return '-';
    const labels = userTypeGUIDs.map(guid => {
      const option = userTypeOptions.find(opt => opt.id === guid);
      return option ? option.label : guid;
    });
    return labels.join(', ');
  }

  function getFormModelLabel(modelGUID) {
    if (!modelGUID) return '-';
    const option = formModelOptions.find(opt => opt.id === modelGUID);
    return option ? option.label : modelGUID;
  }

  function getRangeLabels(rangeGUIDs) {
    if (!rangeGUIDs || rangeGUIDs.length === 0) return '-';
    // Only consider age ranges for display; ignore non-age GUIDs (e.g., weight)
    const ageIds = new Set(ageRangeOptions.map(o => o.id));
    const labels = [];
    for (const guid of rangeGUIDs) {
      if (ageIds.has(guid)) {
        const opt = ageRangeOptions.find(o => o.id === guid);
        if (opt && opt.label) labels.push(opt.label);
      }
    }
    if (labels.length === 0) return '-';
    // De-duplicate while preserving order
    const unique = Array.from(new Set(labels));
    return unique.join(', ');
  }

  function getLanguageLabel(languageGUID) {
    if (!languageGUID) return '-';
    const lang = studyLanguages.find(l => l.languageGUID === languageGUID);
    if (!lang) return '-';
    return `${lang.flag || ''} ${lang.language} (${lang.code})`.trim();
  }

  function getTranslationLanguages(form) {
    if (!form || !Array.isArray(form.translations) || form.translations.length === 0) return getLanguageLabel(form.languageGUID);
    const uniqueLangs = Array.from(new Set(form.translations.map(t => t.languageGUID))).filter(Boolean);
    if (uniqueLangs.length === 0) return getLanguageLabel(form.languageGUID);
    return uniqueLangs
      .map(guid => getLanguageLabel(guid))
      .filter(l => l !== '-')
      .join(', ');
  }

  function getTranslationSummary(form) {
    if (!form) return '-';
    const base = getLanguageLabel(form.languageGUID);
    const list = (!form.translations || !Array.isArray(form.translations) || form.translations.length === 0)
      ? (base ? [base] : [])
      : Array.from(new Set(form.translations.map(t => t.languageGUID)))
          .filter(Boolean)
          .map(guid => getLanguageLabel(guid))
          .filter(l => l !== '-');
    if (list.length === 0 && base) return base;
    const first = list[0] || base || '-';
    const extra = Math.max(0, list.length - 1);
    return extra > 0 ? `${first} (${extra})` : first;
  }

  async function fetchForms(page = 1) {
    if (!$selectedStudy || !$selectedStudy._id) {
      console.warn('No study selected');
      return;
    }
    //console.log('selectedStudy:', $selectedStudy);
    try {
      let url = `${API_BASE_URL}/forms?page=${page}&pageSize=${limit}&sortField=${sortField}&sortOrder=${sortOrder}&studyGUID=${$selectedStudy._id}`;

      if (searchText) {
        url += `&searchText=${encodeURIComponent(searchText)}`;
      }

      if (languageFilterGUID) {
        url += `&languageGUID=${languageFilterGUID}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch forms');
      }

      const responseData = await res.json();

      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;
      forms = responseData.data;
    } catch (err) {
      console.error('Error fetching forms:', err);
      forms = [];
    }
  }

  function handleRefresh() {
    Promise.all([fetchLookupData(), fetchForms(1)]);
  }

  function handleClearSearch() {
    searchText = '';
    fetchForms(1);
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchForms(newPage);
    }
  }

  function handleSort(field) {
    if (sortField === field) {
      sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      sortField = field;
      sortOrder = 'asc';
    }
    fetchForms(1);
  }

  function formatDate(dateString) {
    return new Date(dateString).toLocaleDateString();
  }

  // Form CRUD operations
  function handleCreateForm() {
    editingForm = null;
    showFormEditor = true;
  }

  function handleEditForm(form) {
    editingForm = form;
    showFormEditor = true;
  }

  function handleDeleteForm(form) {
    formToDelete = form;
    console.log('formToDelete:', formToDelete);
    showDeleteConfirmation = true;
  }

  function handleAuditLogClick(form) {
    auditLogForm = form;
    showAuditLog = true;
  }

  async function handleFormSubmit(event) {
    const formData = event.detail;

    try {
      const url = editingForm 
        ? `${API_BASE_URL}/forms/${editingForm._id}`
        : `${API_BASE_URL}/forms`;
      
      const method = editingForm ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to save form');
      }

      // Reset form editor state
      showFormEditor = false;
      editingForm = null;

      // Refresh the forms list
      await fetchForms(currentPage);
    } catch (error) {
      console.error('Error saving form:', error);
    }
  }

  async function confirmDeleteForm() {
    if (!formToDelete) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/forms/${formToDelete._id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete form');
      }

      // Reset confirmation modal state
      showDeleteConfirmation = false;
      formToDelete = null;

      // Refresh the forms list
      await fetchForms(currentPage);
    } catch (error) {
      console.error('Error deleting form:', error);
    }
  }

  function handleCopyFromStudyClick() {
    showStudySelectModal = true;
  }

  async function handleStudySelected(event) {
    const selectedStudyId = event.detail;
    if (!selectedStudyId) {
      return;
    }

    try {
      // Get forms from the selected study
      const res = await fetch(
        `${API_BASE_URL}/forms?studyGUID=${selectedStudyId}`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch forms from selected study');
      }

      const response = await res.json();
      const sourceForms = Array.isArray(response.data) ? response.data : [];
      
      if (sourceForms.length === 0) {
        alert('No forms found in the selected study');
        return;
      }

      // Copy each form to the current study
      for (const form of sourceForms) {
        // Create a new form in the current study
        const newForm = {
          studyGUID: $selectedStudy._id,
          name: `${form.name}`,
          type: form.type,
          form: form.form,
          exportLabel: form.exportLabel,
          active: form.active,
          // Copy new fields if they exist
          typeGUID: form.typeGUID || '',
          userTypeGUIDs: form.userTypeGUIDs || [],
          modelGUID: form.modelGUID || '',
          rangeGUIDs: form.rangeGUIDs || [],
          languageGUID: form.languageGUID || '',
          translations: Array.isArray(form.translations) ? form.translations.map(t => ({ languageGUID: t.languageGUID, form: t.form })) : undefined,
          createDate: new Date(),
          lastUpdateDate: new Date()
        };

        // Create the new form
        const formRes = await fetch(`${API_BASE_URL}/forms`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newForm),
        });

        if (!formRes.ok) {
          console.error(`Failed to copy form: ${form.name}`);
        }
      }

      // Refresh the forms list
      await fetchForms(currentPage);
    } catch (error) {
      console.error('Error copying forms:', error);
    }
  }

  function toggleActionsMenu() {
    showActionsMenu = !showActionsMenu;
  }

  async function handleExportForms() {
    if (!$selectedStudy || !$selectedStudy._id) {
      console.warn('No study selected');
      return;
    }

    try {
      // Fetch all forms for the current study without pagination
      const res = await fetch(
        `${API_BASE_URL}/forms?studyGUID=${$selectedStudy._id}&pageSize=999999`
      );

      if (!res.ok) {
        throw new Error('Failed to fetch forms for export');
      }

      const response = await res.json();
      const formsToExport = Array.isArray(response.data) ? response.data : [];
      
      if (formsToExport.length === 0) {
        alert('No forms found to export');
        return;
      }

      // Create a clean export object
      const exportData = {
        exportDate: new Date().toISOString(),
        sourceStudyId: $selectedStudy._id,
        sourceStudyName: $selectedStudy.name || 'Unknown Study',
        formsCount: formsToExport.length,
        forms: formsToExport.map(form => ({
          name: form.name,
          typeGUID: form.typeGUID || '',
          userTypeGUIDs: form.userTypeGUIDs || [],
          modelGUID: form.modelGUID || '',
          rangeGUIDs: form.rangeGUIDs || [],
          languageGUID: form.languageGUID || '',
          translations: Array.isArray(form.translations) ? form.translations : [],
          type: form.type,
          form: form.form,
          exportLabel: form.exportLabel,
          active: form.active
        }))
      };

      // Create a blob and download it
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `forms-export-${$selectedStudy.name || 'study'}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showActionsMenu = false;
      alert(`Successfully exported ${formsToExport.length} form(s)`);
    } catch (error) {
      console.error('Error exporting forms:', error);
      alert('Failed to export forms. Please try again.');
    }
  }

  function handleImportClick() {
    // Trigger the hidden file input
    if (fileInputRef) {
      fileInputRef.click();
    }
    showActionsMenu = false;
  }

  async function handleFileSelected(event) {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    if (!$selectedStudy || !$selectedStudy._id) {
      alert('Please select a study first');
      return;
    }

    try {
      const fileContent = await file.text();
      const importData = JSON.parse(fileContent);

      if (!importData.forms || !Array.isArray(importData.forms)) {
        throw new Error('Invalid import file format: forms array not found');
      }

      const formsToImport = importData.forms;
      
      if (formsToImport.length === 0) {
        alert('No forms found in the import file');
        return;
      }

      // Confirm import
      const confirmMessage = `This will import ${formsToImport.length} form(s) into the current study "${$selectedStudy.name}". Continue?`;
      if (!confirm(confirmMessage)) {
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      // Import each form into the current study
      for (const form of formsToImport) {
        try {
          const newForm = {
            studyGUID: $selectedStudy._id, // Assign to current study
            name: form.name,
            type: form.type,
            form: form.form,
            exportLabel: form.exportLabel,
            active: form.active,
            typeGUID: form.typeGUID || '',
            userTypeGUIDs: form.userTypeGUIDs || [],
            modelGUID: form.modelGUID || '',
            rangeGUIDs: form.rangeGUIDs || [],
            languageGUID: form.languageGUID || '',
            translations: Array.isArray(form.translations) ? form.translations : [],
            createDate: new Date(),
            lastUpdateDate: new Date()
          };

          const formRes = await fetch(`${API_BASE_URL}/forms`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(newForm),
          });

          if (formRes.ok) {
            successCount++;
          } else {
            errorCount++;
            console.error(`Failed to import form: ${form.name}`);
          }
        } catch (error) {
          errorCount++;
          console.error(`Error importing form ${form.name}:`, error);
        }
      }

      // Reset file input
      event.target.value = '';

      // Show result
      alert(`Import complete!\nSuccessfully imported: ${successCount}\nFailed: ${errorCount}`);

      // Refresh the forms list
      await fetchForms(currentPage);
    } catch (error) {
      console.error('Error importing forms:', error);
      alert('Failed to import forms. Please check the file format and try again.');
      // Reset file input
      event.target.value = '';
    }
  }
</script>

<StudyHeader />

<FeatureInfo 
  title="Forms Management" 
  description="This page allows you to manage all forms in the system. You can search, create, edit, and delete forms. Each form entry includes details such as form name, type, and form data."
  marginBottom="mb-0"
/>

<StudyDetails showOptionsMenu={false} />

<Toolbar title="Forms" itemCountTitle="Forms" itemCount={resultCount}>
  <div class="flex items-center gap-4">
    <div class="relative">
      <input
        type="text"
        placeholder="Search"
        bind:value={searchText}
        on:input={() => fetchForms(1)}
        class="px-3 py-1.5 text-xs text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 pr-8"
      />
      {#if searchText}
        <button
          on:click={handleClearSearch}
          class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          title="Clear search"
        >
          <FontAwesomeIcon icon={faCircleXmark} />
        </button>
      {/if}
    </div>
    <div class="min-w-[220px]">
      <select
        class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
        bind:value={languageFilterGUID}
        on:change={() => fetchForms(1)}
      >
        <option value="">All Languages</option>
        {#each studyLanguages as lang}
          <option value={lang.languageGUID}>{lang.flag} {lang.language} ({lang.code})</option>
        {/each}
      </select>
    </div>
    <button 
      on:click={handleCreateForm} 
      class="pagination mx-1 p-0"
      title="Create new form"
    >
      <FontAwesomeIcon icon={faSquarePlus} />
    </button>
    {#if isAdmin}
      <div class="relative">
        <button 
          class="pagination mx-1 p-0" 
          on:click={toggleActionsMenu}
          title="More actions (Admin only)"
        >
          <FontAwesomeIcon icon={faEllipsisVertical} />
        </button>
        {#if showActionsMenu}
          <div class="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg z-50 border border-gray-200 dark:border-gray-700">
            <div class="py-1">
              <button
                on:click={handleCopyFromStudyClick}
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
              >
                <FontAwesomeIcon icon={faCopy} class="w-4" />
                Copy forms from another study
              </button>
              <button
                on:click={handleExportForms}
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
              >
                <FontAwesomeIcon icon={faFileExport} class="w-4" />
                Export forms to JSON
              </button>
              <button
                on:click={handleImportClick}
                class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3"
              >
                <FontAwesomeIcon icon={faFileImport} class="w-4" />
                Import forms from JSON
              </button>
            </div>
          </div>
        {/if}
      </div>
    {/if}
    <div class="text-white dark:text-white">
      <button on:click={handleRefresh} class="pagination mx-1 p-0">
        <FontAwesomeIcon icon={faRotate} />
      </button>
    </div>
  </div>
</Toolbar>

<div class="px-1 pt-0 overflow-x-auto">
  <table class="min-w-full border-collapse table-auto">
    <thead>
      <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
        <th class="px-4 py-2 text-left bold cursor-pointer" on:click={() => handleSort('name')}>
          Name {sortField === 'name' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
        </th>
        <th class="px-4 py-2 text-left bold">Form Type</th>
        <th class="px-4 py-2 text-left bold">User Types</th>
        <th class="px-4 py-2 text-left bold">Form Model</th>
        <th class="px-4 py-2 text-left bold">Range Definitions</th>
        <th class="px-4 py-2 text-left bold">Language</th>
        <th class="px-4 py-2 text-left bold">Status</th>
        <th class="px-4 py-2 text-left bold">Actions</th>
      </tr>
    </thead>
    <tbody>
      {#each forms as form, index}
        <tr
          class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
            2 ===
          0
            ? 'bg-stone-50 dark:bg-gray-725'
            : 'bg-stone-75 dark:bg-gray-750'}"
        >
          <td class="px-4 py-2">{form.name}</td>
          <td class="px-4 py-2">{getFormTypeLabel(form.typeGUID)}</td>
          <td class="px-4 py-2 max-w-xs truncate" title={getUserTypeLabels(form.userTypeGUIDs)}>
            {getUserTypeLabels(form.userTypeGUIDs)}
          </td>
          <td class="px-4 py-2">{getFormModelLabel(form.modelGUID)}</td>
          <td class="px-4 py-2 max-w-xs truncate" title={getRangeLabels(form.rangeGUIDs)}>
            {getRangeLabels(form.rangeGUIDs)}
          </td>
          <td class="px-4 py-2" title={getTranslationLanguages(form)}>{getTranslationSummary(form)}</td>
          <td class="px-4 py-2">
            <span
              class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
              class:text-green-700={form.active}
              class:ring-green-700={form.active}
              class:text-red-600={!form.active}
              class:ring-red-600={!form.active}
            >
              {form.active ? 'Active' : 'Inactive'}
            </span>
          </td>
          <td class="px-4 py-2">
            <div class="flex items-center space-x-2">
              <button 
                on:click={() => handleEditForm(form)} 
                title="Edit Form"
                disabled={isReadOnly()}
                class:opacity-50={isReadOnly()}
              >
                <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
              </button>
              <button
                on:click={() => handleAuditLogClick(form)}
                class="pagination mx-1 p-0"
                title="View audit logs"
                disabled={!canViewAuditLogs}
                class:opacity-50={!canViewAuditLogs}
              >
                <FontAwesomeIcon icon={faHistory} class="text-sky-800 dark:text-white/70" />
              </button>
              <button 
                on:click={() => handleDeleteForm(form)} 
                title="Delete Form"
                disabled={isReadOnly()}
                class:opacity-50={isReadOnly()}
              >
                <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
              </button>
            </div>
          </td>
        </tr>
      {/each}
      {#if forms.length === 0}
        <tr>
          <td colspan="8" class="text-center text-gray-500 dark:text-slate-400 mt-10 py-10">
            No forms found
          </td>
        </tr>
      {/if}
    </tbody>
  </table>
</div>

<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
</Footer>

<!-- Form Editor Modal -->
<FormEditor
  bind:showForm={showFormEditor}
  bind:editForm={editingForm}
  on:close={() => {
    showFormEditor = false;
    editingForm = null;
  }}
  on:submit={handleFormSubmit}
/>

<!-- Delete Confirmation Modal -->
<ConfirmationModal
  bind:showConfirmation={showDeleteConfirmation}
  message={`Are you sure you want to delete the form "${formToDelete?.name}"? This action cannot be undone.`}
  confirmPhrase="delete"
  on:close={() => {
    showDeleteConfirmation = false;
    formToDelete = null;
  }}
  on:confirm={confirmDeleteForm}
/>

<!-- Study Select Modal -->
<StudySelectModal
  bind:showModal={showStudySelectModal}
  currentStudyId={$selectedStudy?._id}
  on:studySelected={handleStudySelected}
  on:close={() => {
    showStudySelectModal = false;
  }}
/>

<!-- Audit Log Modal -->
{#if showAuditLog}
  <AuditLogModal
    showModal={showAuditLog}
    documentId={auditLogForm?._id}
    collection="Form"
    title={`Audit Logs - ${auditLogForm?.name || 'Form'}`}
    on:close={() => (showAuditLog = false)}
  />
{/if}

<!-- Hidden file input for import -->
<input
  type="file"
  accept=".json"
  bind:this={fileInputRef}
  on:change={handleFileSelected}
  class="hidden"
/>

<!-- Click outside handler for menu -->
{#if showActionsMenu}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div 
    class="fixed inset-0 z-40" 
    on:click={() => showActionsMenu = false}
  />
{/if}
